import React, { useState } from 'react';
import { useUpdateCourse } from '../api/coursesApi';
import { Course } from '../types';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/features/auth/components/ErrorAlert';

interface EditCourseFormProps {
  course: Course;
  onSuccess?: () => void;
}

const EditCourseForm: React.FC<EditCourseFormProps> = ({ course, onSuccess }) => {
  const [name, setName] = useState(course.name);
  const [lectureCount, setLectureCount] = useState<number>(course.lectureCount);
  const [watchedLectures, setWatchedLectures] = useState<number>(course.watchedLectures);
  const [error, setError] = useState<string | null>(null);
  
  const updateCourseMutation = useUpdateCourse();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter a course name');
      return;
    }

    if (lectureCount < 1) {
      setError('Lecture count must be at least 1');
      return;
    }

    if (watchedLectures < 0) {
      setError('Watched lectures cannot be negative');
      return;
    }

    if (watchedLectures > lectureCount) {
      setError('Watched lectures cannot exceed total lectures');
      return;
    }

    try {
      await updateCourseMutation.mutateAsync({ 
        courseId: course.id,
        updates: {
          name: name.trim(), 
          lectureCount,
          watchedLectures
        }
      });
      onSuccess?.(); // Call success callback (e.g., close modal)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  // Check if form has changes
  const hasChanges = name.trim() !== course.name || 
                    lectureCount !== course.lectureCount || 
                    watchedLectures !== course.watchedLectures;

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="edit-course-name" className="block text-sm font-medium text-gray-700 mb-2">
            Course Name
          </label>
          <input
            type="text"
            id="edit-course-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter course name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={updateCourseMutation.isPending}
          />
        </div>

        <div>
          <label htmlFor="edit-lecture-count" className="block text-sm font-medium text-gray-700 mb-2">
            Total Lectures
          </label>
          <input
            type="number"
            id="edit-lecture-count"
            value={lectureCount}
            onChange={(e) => setLectureCount(parseInt(e.target.value, 10) || 1)}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={updateCourseMutation.isPending}
          />
          <p className="mt-1 text-sm text-gray-500">
            How many lectures does this course have?
          </p>
        </div>

        <div>
          <label htmlFor="edit-watched-lectures" className="block text-sm font-medium text-gray-700 mb-2">
            Lectures Watched
          </label>
          <input
            type="number"
            id="edit-watched-lectures"
            value={watchedLectures}
            onChange={(e) => setWatchedLectures(parseInt(e.target.value, 10) || 0)}
            min="0"
            max={lectureCount}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={updateCourseMutation.isPending}
          />
          <p className="mt-1 text-sm text-gray-500">
            Number of lectures you've already watched
          </p>
        </div>

        <ErrorAlert message={error ?? ''} />

        {updateCourseMutation.isSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">Course updated successfully!</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button 
            type="submit" 
            variant="primary" 
            disabled={updateCourseMutation.isPending || !name.trim() || lectureCount < 1 || watchedLectures < 0 || watchedLectures > lectureCount || !hasChanges} 
            className="flex-1"
          >
            {updateCourseMutation.isPending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </span>
            ) : (
              'Update Course'
            )}
          </Button>
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={onSuccess}
            disabled={updateCourseMutation.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
      
      <div className="mt-4 text-sm text-gray-500">
        Make changes to your course details and click update to save
      </div>
    </div>
  );
};

export default EditCourseForm; 